import { useEffect, useRef, useState } from "react";

/**
 * Intersection observer hook
 */
export function useInView(options = {}) {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Fast-path: if the element is already in the viewport on mount,
    // show immediately. Prevents "stuck invisible" from observer misfire.
    const checkInViewport = () => {
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const vw = window.innerWidth || document.documentElement.clientWidth;
      return r.top < vh && r.bottom > 0 && r.left < vw && r.right > 0;
    };

    if (checkInViewport()) {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsInView(true); observer.unobserve(el); } },
      { threshold: 0.1, ...options }
    );
    observer.observe(el);

    // Safety net 1: re-check on every resize/scroll (catches cases where
    // observer didn't fire for some reason)
    const recheck = () => {
      if (checkInViewport()) {
        setIsInView(true);
        observer.disconnect();
        window.removeEventListener("scroll", recheck, { passive: true });
        window.removeEventListener("resize", recheck);
      }
    };
    window.addEventListener("scroll", recheck, { passive: true });
    window.addEventListener("resize", recheck);

    // Safety net 2: after 2.5s, force visible regardless. Users should never
    // see permanently invisible content — better a harmless late fade-in
    // than a ghost page.
    const forceTimer = setTimeout(() => {
      setIsInView(true);
      observer.disconnect();
    }, 2500);

    return () => {
      observer.disconnect();
      clearTimeout(forceTimer);
      window.removeEventListener("scroll", recheck);
      window.removeEventListener("resize", recheck);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [ref, isInView];
}

function isMobile() {
  return typeof window !== 'undefined' && window.innerWidth < 768;
}

/**
 * Reveal an element when it scrolls into view.
 * Direction options: up, down, left, right, scale, rotate, blur, flip, none
 * Default latency: 180ms feel (ease-out, duration 0.72s)
 */
export function AnimateIn({ children, className = "", delay = 0, direction = "up", duration = 0.72 }) {
  const [ref, isInView] = useInView();

  const safeDirection = isMobile() && (direction === "left" || direction === "right") ? "up" : direction;

  const transforms = {
    up: "translateY(34px)",
    down: "translateY(-34px)",
    left: "translateX(-44px)",
    right: "translateX(44px)",
    scale: "scale(0.88)",
    rotate: "rotate(-4deg) translateY(20px)",
    blur: "translateY(18px)",
    flip: "perspective(800px) rotateX(20deg)",
    none: "none",
  };

  const filters = {
    blur: isInView ? "blur(0)" : "blur(8px)",
  };

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isInView ? 1 : 0,
        transform: isInView ? "none" : transforms[safeDirection],
        filter: direction === "blur" ? filters.blur : "none",
        transition: `opacity ${duration}s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s, transform ${duration}s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s, filter ${duration}s ease ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

/**
 * Stagger children with a precise delay (seconds).
 * Default 0.18s (180ms) to match Swiss Brutalist pacing.
 */
export function StaggerChildren({ children, className = "", staggerDelay = 0.18, baseDelay = 0, direction = "up" }) {
  const [ref, isInView] = useInView();

  const transforms = {
    up: "translateY(24px)",
    left: "translateX(-30px)",
    right: "translateX(30px)",
    scale: "scale(0.94)",
    none: "none",
  };

  return (
    <div ref={ref} className={className}>
      {Array.isArray(children)
        ? children.map((child, i) => (
            <div
              key={i}
              style={{
                opacity: isInView ? 1 : 0,
                transform: isInView ? "none" : transforms[direction] || transforms.up,
                transition: `opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1) ${baseDelay + i * staggerDelay}s, transform 0.6s cubic-bezier(0.22, 1, 0.36, 1) ${baseDelay + i * staggerDelay}s`,
              }}
            >
              {child}
            </div>
          ))
        : children}
    </div>
  );
}

/**
 * Hook that returns { ref, isInView, getStagger(i) } - use this when you want
 * to stagger dom items yourself with the 180ms cadence.
 */
export function useStaggerReveal(baseDelay = 0, step = 0.18) {
  const [ref, isInView] = useInView();
  const getStagger = (i) => ({
    opacity: isInView ? 1 : 0,
    transform: isInView ? "translateY(0)" : "translateY(22px)",
    transition: `opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1) ${baseDelay + i * step}s, transform 0.6s cubic-bezier(0.22, 1, 0.36, 1) ${baseDelay + i * step}s`,
  });
  return { ref, isInView, getStagger };
}

/**
 * Magnetic hover: follow cursor slightly when hovered.
 */
export function useMagnetic(strength = 0.25) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || isMobile()) return;

    const handleMove = (e) => {
      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;
      el.style.transform = `translate(${x * strength}px, ${y * strength}px)`;
    };
    const handleLeave = () => {
      el.style.transform = "translate(0, 0)";
    };

    el.addEventListener("mousemove", handleMove);
    el.addEventListener("mouseleave", handleLeave);
    return () => {
      el.removeEventListener("mousemove", handleMove);
      el.removeEventListener("mouseleave", handleLeave);
    };
  }, [strength]);

  return ref;
}
